'use client';

import { useState, useMemo, useEffect } from 'react';
import type { CmsRoom, CmsRoomCategory } from '@/lib/cms';
import RoomCard from '@/components/RoomCard';
import siteTheme from '@/lib/theme';
import { Star } from 'lucide-react';
import ArrowIcon from '@/components/ArrowIcon';
const { colors, fonts } = siteTheme;

interface RoomsGridProps {
  rooms: CmsRoom[];
  categories?: CmsRoomCategory[];
  showFilterTabs?: boolean;
  itemsPerPage?: number;
  defaultCategory?: string;
  roomStatuses?: Record<number, 'available' | 'fully_booked' | 'maintenance'>;
  checkin?: string;
  checkout?: string;
}

export default function RoomsGrid({ rooms, categories, showFilterTabs = true, itemsPerPage = 9, defaultCategory = 'All', roomStatuses, checkin, checkout }: RoomsGridProps) {
  const ROOMS_PER_PAGE = itemsPerPage;

  // If defaultCategory doesn't match any room, fall back to 'All'
  const safeDefault = (!defaultCategory || defaultCategory === 'All' || rooms.some((r) => r.category?.name === defaultCategory))
    ? (defaultCategory || 'All')
    : 'All';

  const [activeCategory, setActiveCategory] = useState<string>(safeDefault);
  const [page, setPage]                     = useState(1);
  const derivedCategories = useMemo(() => {
    // If CMS categories are provided, use them (ordered by sort_order)
    if (categories?.length) {
      return ['All', ...categories.map((c) => c.name)];
    }
    // Fallback: derive from rooms data
    const seen = new Set<string>();
    const list: string[] = ['All'];
    for (const room of rooms) {
      const name = room.category?.name;
      if (name && !seen.has(name)) {
        seen.add(name);
        list.push(name);
      }
    }
    return list;
  }, [rooms, categories]);

  const categoryList = derivedCategories;

  const filtered = useMemo(() => {
    return activeCategory === 'All' ? rooms : rooms.filter((r) => r.category?.name === activeCategory);
  }, [rooms, activeCategory]);

  useEffect(() => { setPage(1); }, [activeCategory]);

  const totalPages = Math.ceil(filtered.length / ROOMS_PER_PAGE);
  const visible    = filtered.slice(0, page * ROOMS_PER_PAGE);

  /* ── Empty state ── */
  if (rooms.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 'clamp(5rem, 8vw, 8rem) 0' }}>
        <p style={{ fontFamily: fonts.sans, fontSize: '1.6rem', fontWeight: 300, color: colors.goldLight }}>
          No rooms are available at the moment.
        </p>
      </div>
    );
  }

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: `
        .rooms-section {
          background-color: var(--bg);
          padding: clamp(5rem, 8vw, 10rem) 0;
        }
        .rooms-tab-bar-wrap {
          border-top: 1px solid var(--gold-pale);
          border-bottom: 1px solid var(--gold-pale);
          margin-bottom: clamp(3rem, 5vw, 6rem);
          overflow: hidden;
        }
        .rooms-tab-bar {
          display: flex;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
          -ms-overflow-style: none;
          max-width: 144rem;
          margin: 0 auto;
          padding: 0 clamp(2rem, 6vw, 8rem);
        }
        .rooms-tab-bar::-webkit-scrollbar { display: none; }
        .rooms-tab-btn {
          font-family: var(--font-sans);
          font-size: 1.3rem;
          font-weight: 400;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          white-space: nowrap;
          flex-shrink: 0;
          padding: 1.6rem clamp(1.8rem, 2.5vw, 3.6rem);
          cursor: pointer;
          border: none;
          border-right: 1px solid var(--gold-pale);
          outline: none;
          transition: background-color 0.2s ease, color 0.2s ease;
          display: flex;
          align-items: center;
          gap: 0.8rem;
        }
        .rooms-tab-btn:last-child { border-right: none; }
        .rooms-tab-btn.active  { background-color: var(--gold); color: #fff; }
        .rooms-tab-btn:not(.active) { background-color: transparent; color: var(--gold); }
        .rooms-tab-btn:not(.active):hover { background-color: var(--bg-alt); }
        .rooms-inner {
          max-width: 144rem;
          margin: 0 auto;
          padding: 0 clamp(2rem, 6vw, 8rem);
        }
        .rooms-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: clamp(1.6rem, 2.5vw, 3.2rem);
          margin-bottom: clamp(3rem, 5vw, 6rem);
        }
        @media (max-width: 1100px) { .rooms-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 640px)  { .rooms-grid { grid-template-columns: 1fr; } }
        .rooms-pagination {
          display: flex;
          align-items: center;
          gap: 2.4rem;
          flex-wrap: wrap;
        }
      ` }} />

      <section className="rooms-section">

        {/* ── Category Tab Bar ── */}
        {showFilterTabs && categoryList.length > 1 && (
          <div className="rooms-tab-bar-wrap">
            <div className="rooms-tab-bar">
              {categoryList.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`rooms-tab-btn${activeCategory === cat ? ' active' : ''}`}
                >
                  {activeCategory === cat && (
                    <Star size={11} fill="currentColor" strokeWidth={0} style={{ flexShrink: 0, opacity: 0.85 }} />
                  )}
                  {cat}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Inner container: grid / carousel ── */}
        <div className="rooms-inner">

          {/* ── Grid (1-col on mobile, 2-col tablet, 3-col desktop) ── */}
          {visible.length > 0 ? (
            <div className="rooms-grid">
              {visible.map((room) => (
                <RoomCard
                  key={room.id}
                  room={room}
                  status={roomStatuses ? (roomStatuses[room.id] ?? 'not_setup') : undefined}
                  checkin={checkin}
                  checkout={checkout}
                />
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '6rem 0' }}>
              <p style={{ fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 300, color: colors.goldLight }}>No rooms found in this category.</p>
            </div>
          )}

          {totalPages > 1 && (
            <div className="rooms-pagination">
              <span style={{ fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 300, color: colors.goldLight }}>
                {page} / {totalPages}
              </span>
              {page < totalPages && (
                <button onClick={() => setPage((p) => p + 1)} className="btn-luxury-gold" style={{
                  fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 400,
                  letterSpacing: '0.12em', textTransform: 'uppercase',
                  color: colors.gold, background: 'none',
                  border: `1px solid ${colors.goldPale}`, borderRadius: '0.4rem',
                  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.6rem',
                  padding: '0.9rem 2.4rem',
                }}>
                  Show more <ArrowIcon variant="short" />
                </button>
              )}
            </div>
          )}

        </div>
      </section>
    </>
  );
}
